import React, { useState } from "react";
import { format } from "date-fns";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

type TabType = "run_payroll" | "payroll_history";

const DoctorPayouts: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("run_payroll");

  const currentMonth = format(new Date(), "MMMM yyyy");
  const lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
  const payDay = Math.min(30, lastDayOfMonth.getDate());
  const payDate = format(
    new Date(new Date().getFullYear(), new Date().getMonth(), payDay),
    "dd/MM/yyyy"
  );
  const doctorCount = 4;
  const netPay = "YET TO PROCESS";
  const status = "READY";
  const isPastPayDay = new Date().getDate() > 30;

  const handleCreatePayRun = () => {
    // TODO: Wire to API when available
    console.log("Create Pay Run clicked");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex-auto">
        <h1 className="text-base font-semibold leading-6 text-gray-900">
          Doctor Payouts
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Generate pay runs and manage doctor payout history.
        </p>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-8" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab("run_payroll")}
            className={`whitespace-nowrap border-b-2 py-4 text-sm font-medium ${
              activeTab === "run_payroll"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            Run Payroll
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("payroll_history")}
            className={`whitespace-nowrap border-b-2 py-4 text-sm font-medium ${
              activeTab === "payroll_history"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            Payroll History
          </button>
        </nav>
      </div>

      {/* Run Payroll Tab Content */}
      {activeTab === "run_payroll" && (
        <div className="mt-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-base font-semibold text-gray-900">
                    Process Pay Run for {currentMonth}
                  </h2>
                  <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-white">
                    {status}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleCreatePayRun}
                  className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  Create Pay Run
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-8">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Doctors&apos; Net Pay
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-400">
                    {netPay}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Payment Date
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900">
                    {payDate}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    No. of Doctors
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900">
                    {doctorCount}
                  </p>
                </div>
              </div>

              {isPastPayDay && (
                <div className="mt-6 flex items-start gap-2 rounded-md bg-gray-50 px-3 py-2">
                  <InformationCircleIcon className="h-5 w-5 shrink-0 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    You haven&apos;t processed this pay run and it&apos;s past the pay day.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payroll History Tab Content */}
      {activeTab === "payroll_history" && (
        <div className="mt-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-6">
              <p className="text-sm text-gray-500">
                Past pay runs will appear here once processed.
              </p>
              <div className="mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Period
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Payment Date
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        No. of Doctors
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Net Pay
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-sm text-gray-500">
                        No payroll history yet.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorPayouts;
